import React from 'react';
import { motion } from 'framer-motion';
import { GameState } from '../../types';
import { UserProfile } from '../../types/profile';
import { getMeasurementTypeDisplay } from '../../utils/gameLogic';

interface ResultsScreenProps {
  gameState: GameState;
  onPlayAgain: () => void;
  onBackToHome: () => void;
  profile?: UserProfile | null;
}

const ResultsScreen: React.FC<ResultsScreenProps> = ({ gameState, onPlayAgain, onBackToHome, profile }) => {
  const getPerformanceMessage = () => {
    const accuracy = gameState.questionsAnswered > 0 ? (gameState.score / (gameState.questionsAnswered * 100)) * 100 : 0;
    
    if (accuracy >= 90) return { message: "Outstanding! 🏆", color: "text-yellow-600" };
    if (accuracy >= 80) return { message: "Excellent! 🌟", color: "text-green-600" };
    if (accuracy >= 70) return { message: "Great Job! 👏", color: "text-blue-600" };
    if (accuracy >= 60) return { message: "Good Work! 👍", color: "text-purple-600" };
    if (accuracy >= 50) return { message: "Not Bad! 🤔", color: "text-orange-600" };
    return { message: "Keep Practicing! 💪", color: "text-red-600" };
  };

  const performance = getPerformanceMessage();
  const averageScore = gameState.questionsAnswered > 0 ? Math.round(gameState.score / gameState.questionsAnswered) : 0;

  // Check if this is a new personal best
  const isNewBest = profile && gameState.score > profile.stats.bestScore;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <motion.div 
        className="text-center mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-4xl font-bold text-banana-800 mb-4">Game Complete! 🎉</h2>
        <p className={`text-2xl font-bold ${performance.color}`}>{performance.message}</p>
        
        {/* New Personal Best Indicator */}
        {isNewBest && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, type: "spring" }}
            className="mt-4 inline-block bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-4 py-2 rounded-full font-bold"
          >
            🏆 New Personal Best! 🏆
          </motion.div>
        )}
      </motion.div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <motion.div 
          className="card text-center bg-gradient-to-br from-banana-50 to-banana-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="text-3xl mb-2">🎯</div>
          <h3 className="text-2xl font-bold text-banana-800">{gameState.score}</h3>
          <p className="text-banana-600">Total Score</p>
        </motion.div>

        <motion.div 
          className="card text-center bg-gradient-to-br from-green-50 to-green-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="text-3xl mb-2">📊</div>
          <h3 className="text-2xl font-bold text-green-800">{averageScore}</h3>
          <p className="text-green-600">Avg. per Question</p>
        </motion.div>

        <motion.div 
          className="card text-center bg-gradient-to-br from-blue-50 to-blue-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="text-3xl mb-2">🔥</div>
          <h3 className="text-2xl font-bold text-blue-800">{gameState.streak}</h3>
          <p className="text-blue-600">Best Streak</p>
        </motion.div>

        <motion.div 
          className="card text-center bg-gradient-to-br from-purple-50 to-purple-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="text-3xl mb-2">✅</div>
          <h3 className="text-2xl font-bold text-purple-800">{gameState.questionsAnswered}</h3>
          <p className="text-purple-600">Questions Answered</p>
        </motion.div>
      </div>

      {/* Game Mode Info */}
      <motion.div 
        className="card mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
      >
        <h3 className="text-xl font-bold text-banana-800 mb-4 text-center">Game Summary</h3>
        <div className="grid md:grid-cols-3 gap-4 text-center">
          <div>
            <h4 className="font-semibold text-banana-700">Game Mode</h4>
            <p className="text-banana-600 capitalize">{gameState.gameMode.replace('_', ' ')}</p>
          </div>
          <div>
            <h4 className="font-semibold text-banana-700">Difficulty</h4>
            <p className="text-banana-600 capitalize">{gameState.difficulty}</p>
          </div>
          <div>
            <h4 className="font-semibold text-banana-700">Measurement Focus</h4>
            <p className="text-banana-600">
              {gameState.currentMeasurementType 
                ? getMeasurementTypeDisplay(gameState.currentMeasurementType).name
                : 'Mixed'
              }
            </p>
          </div>
        </div>
      </motion.div>

      {/* Action Buttons */}
      <motion.div 
        className="flex flex-col sm:flex-row gap-4 justify-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <button
          className="btn-primary px-8 py-3 text-lg"
          onClick={onPlayAgain}
        >
          🔄 Play Again
        </button>
        <button
          className="btn-secondary px-8 py-3 text-lg"
          onClick={onBackToHome}
        >
          🏠 Back to Home
        </button>
      </motion.div>

      {/* Encouragement Message */}
      <motion.div 
        className="text-center mt-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.8 }}
      >
        <p className="text-banana-700">
          {gameState.score >= averageScore * gameState.questionsAnswered * 0.8 
            ? "You have great intuition about object sizes! 🧠✨"
            : "Keep playing to improve your spatial reasoning skills! 🚀"
          }
        </p>
      </motion.div>
    </div>
  );
};

export default ResultsScreen; 